import { AnimaliaListItem } from '../../domain/entities/AnimaliaListItem';
import {
  AnimaliaNavigableTaxonomyDTO,
  createAnimaliaNavigableTaxonomy,
} from '../taxonomy/AnimaliaNavigableTaxonomy';

export interface AnimaliaDetailPillDTO {
  filterKey: AnimaliaNavigableTaxonomyDTO['filterKey'];
  label: string;
  value: string;
}

export interface AnimaliaDetailHeroDTO {
  title: string;
  subtitle: string | null;
  imageSrc: string | null;
  imageAlt: string;
  imageLicenseName: string | null;
  imageLicenseUrl: string | null;
  imageArtist: string | null;
  imageCredit: string | null;
  imageAttributionRequired: boolean;
  imageSource: string | null;
  wikipediaUrl: string | null;
  pills: ReadonlyArray<AnimaliaDetailPillDTO>;
}

export interface AnimaliaDetailFieldDTO {
  label: string;
  value: string;
  href: string | null;
}

export interface AnimaliaDetailTaxonomyStepDTO {
  filterKey: AnimaliaNavigableTaxonomyDTO['filterKey'];
  label: AnimaliaNavigableTaxonomyDTO['label'];
  value: AnimaliaNavigableTaxonomyDTO['value'];
  isFinal: boolean;
}

export interface AnimaliaDetailNarrativeBlockDTO {
  indexLabel: string;
  title: string;
  body: string;
}

export interface AnimaliaDetailPageDTO {
  slug: string;
  hero: AnimaliaDetailHeroDTO;
  identity: ReadonlyArray<AnimaliaDetailFieldDTO>;
  references: ReadonlyArray<AnimaliaDetailFieldDTO>;
  taxonomy: ReadonlyArray<AnimaliaDetailTaxonomyStepDTO>;
  narrativeBlocks: ReadonlyArray<AnimaliaDetailNarrativeBlockDTO>;
}

export class AnimaliaDetailMapper {
  static toPageDTO(entity: AnimaliaListItem): AnimaliaDetailPageDTO {
    const genus = entity.getTaxonomyValue('genus') ?? entity.getGenus();
    const species = entity.getSpeciesLabel();
    const taxonomy = entity.taxonomyEntries.reduce<AnimaliaDetailTaxonomyStepDTO[]>((steps, entry) => {
      const taxonomyEntry = createAnimaliaNavigableTaxonomy(entry.level, entry.value);

      if (!taxonomyEntry) {
        return steps;
      }

      steps.push({
        ...taxonomyEntry,
        isFinal: false,
      });

      return steps;
    }, []);

    if (taxonomy.length > 0) {
      taxonomy[taxonomy.length - 1] = {
        ...taxonomy[taxonomy.length - 1],
        isFinal: true,
      };
    }

    const narrativeBlocks =
      entity.narrativeBlocks.length > 0
        ? entity.narrativeBlocks.map((block, index) => ({
            indexLabel: String(index + 1).padStart(2, '0'),
            title: block.title,
            body: block.body,
          }))
        : [
            {
              indexLabel: '01',
              title: 'Narrativa editorial en preparacion',
              body:
                'Esta ficha ya esta disponible para consulta, pero todavia no publica bloques narrativos adicionales para profundizar en la especie.',
            },
          ];

    return {
      slug: entity.slug,
      hero: {
        title: entity.scientificName,
        subtitle: entity.commonName,
        imageSrc: entity.image?.src ?? null,
        imageAlt: entity.image?.alt ?? entity.scientificName,
        imageLicenseName: entity.image?.license?.name ?? null,
        imageLicenseUrl: entity.image?.license?.url ?? null,
        imageArtist: entity.image?.license?.artist ?? null,
        imageCredit: entity.image?.license?.credit ?? null,
        imageAttributionRequired: entity.image?.license?.attributionRequired ?? false,
        imageSource: entity.image?.license?.source ?? null,
        wikipediaUrl: entity.wikipedia?.url ?? null,
        pills: [
          createAnimaliaNavigableTaxonomy('family', entity.family),
          createAnimaliaNavigableTaxonomy('genus', genus),
          createAnimaliaNavigableTaxonomy('species', species),
        ].filter((pill): pill is AnimaliaDetailPillDTO => pill !== null),
      },
      identity: [
        {
          label: 'Nombre cientifico',
          value: entity.scientificName,
          href: null,
        },
        {
          label: 'Nombre comun',
          value: entity.commonName ?? 'Sin dato',
          href: null,
        },
        entity.authority
          ? {
              label: 'Autoridad cientifica',
              value: entity.authority,
              href: null,
            }
          : null,
        entity.taxonomicRank
          ? {
              label: 'Rango taxonomico',
              value: entity.taxonomicRank,
              href: null,
            }
          : null,
      ].reduce<AnimaliaDetailFieldDTO[]>((fields, field) => {
        if (field !== null) {
          fields.push(field);
        }

        return fields;
      }, []),
      references: [
        entity.country
          ? {
              label: 'Pais',
              value: entity.country,
              href: null,
            }
          : null,
        entity.continent
          ? {
              label: 'Continente',
              value: entity.continent,
              href: null,
            }
          : null,
      ].reduce<AnimaliaDetailFieldDTO[]>((fields, field) => {
        if (field !== null) {
          fields.push(field);
        }

        return fields;
      }, []),
      taxonomy,
      narrativeBlocks,
    };
  }
}
